import { createContext, useContext, ReactNode, useEffect, useState, useCallback } from "react";
import { useApp } from "./AppContext";

/**
 * ctf challenge type
 */
export interface CTFChallenge {
  id: string;
  title: string;
  category: string;
  description: string;
  isCompleted: boolean;
  attemptsUsed: number;
  attemptsRemaining: number | null;
  maxAttempts: number | null;
  canSubmit: boolean;
  score: number;
}

export interface CTFFlagResult {
  challengeId: string;
  isCorrect: boolean;
  message: string;
  attemptsRemaining: number | null;
  isCompleted: boolean;
}

export interface ScoreboardEntry {
  name: string;
  score: number;
}

export interface CTFSettings {
  displayName: string | null;
  isOptedIn: boolean;
}

export interface CTFContextType {
  loading: boolean;
  scoreboardLoading: boolean;
  error: string | null;
  challenges: CTFChallenge[];
  scoreboard: ScoreboardEntry[];
  lastResult: CTFFlagResult | null;
  submitting: boolean;
  userScore: number;
  settings: CTFSettings;

  // actions
  fetchChallenges: () => void;
  fetchScoreboard: () => void;
  submitFlag: (challengeId: string, guess: string) => void;
  updateSettings: (displayName?: string, isOptedIn?: boolean) => void;
  clearError: () => void;
  clearResult: () => void;
}

const CTFContext = createContext<CTFContextType | undefined>(undefined);

export function useCTF() {
  const context = useContext(CTFContext);
  if (!context) {
    throw new Error("useCTF must be used within a CTFProvider");
  }
  return context;
}

export default function CTFProvider({ children }: { children: ReactNode }) {
  const { sessionReady, lastAuthTime, onMessage, sendMessage } = useApp();

  const [loading, setLoading] = useState(false);
  const [scoreboardLoading, setScoreboardLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [challenges, setChallenges] = useState<CTFChallenge[]>([]);
  const [scoreboard, setScoreboard] = useState<ScoreboardEntry[]>([]);
  const [lastResult, setLastResult] = useState<CTFFlagResult | null>(null);
  const [settings, setSettings] = useState<CTFSettings>({
    displayName: null,
    isOptedIn: false,
  });

  // ===== DATA FETCHING =====
  const fetchChallenges = useCallback(() => {
    if (!sessionReady) {
      console.warn("[CTF] cannot fetch: session not ready");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      sendMessage("ctf", "get_challenges", {});
    } catch (err) {
      console.error("[CTF] failed to fetch:", err);
      setError("failed to fetch challenges");
      setLoading(false);
    }
  }, [sessionReady, sendMessage]);

  const fetchScoreboard = useCallback(() => {
    if (!sessionReady) {
      console.warn("[CTF] cannot fetch scoreboard: session not ready");
      return;
    }

    setScoreboardLoading(true);
    setError(null);

    try {
      sendMessage("ctf", "get_scoreboard", {});
    } catch (err) {
      console.error("[CTF] failed to fetch scoreboard:", err);
      setError("failed to fetch scoreboard");
      setScoreboardLoading(false);
    }
  }, [sessionReady, sendMessage]);

  // ===== MESSAGE HANDLERS =====
  const handleCTFMessage = useCallback((message: any) => {
    // only handle ctf service messages
    if (message.service !== "ctf") return;

    if (message.action === "challenges_list") {
      const challengesData = message.payload?.challenges || [];
      setChallenges(challengesData);
      
      // also load settings if included
      if (message.payload?.settings) {
        setSettings(message.payload.settings);
      }
      
      setLoading(false);
      return;
    }

    if (message.action === "scoreboard_list") {
      const scoreboardData = message.payload?.scoreboard || [];
      setScoreboard(scoreboardData);
      setScoreboardLoading(false);
      return;
    }

    if (message.action === "flag_result") {
      const result = message.payload as CTFFlagResult;
      setLastResult(result);
      setSubmitting(false);

      // refresh challenges after submission
      if (sessionReady) {
        sendMessage("ctf", "get_challenges", {});
      }
      return;
    }

    if (message.action === "settings") {
      const settingsData = message.payload as CTFSettings;
      setSettings(settingsData);
      return;
    }

    if (message.action === "error") {
      const errorMessage = message.payload?.message || "unknown error";
      console.error("[CTF] error:", errorMessage);
      setError(errorMessage);
      setLoading(false);
      setSubmitting(false);
      setScoreboardLoading(false);
      // auto-clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
      return;
    }
  }, [sessionReady, sendMessage]);

  // submit flag
  const submitFlag = useCallback(
    (challengeId: string, guess: string) => {
      if (!sessionReady) {
        console.warn("[CTF] cannot submit: session not ready");
        return;
      }

      setSubmitting(true);
      setError(null);

      try {
        sendMessage("ctf", "submit_flag", { challengeId, guess });
      } catch (err) {
        console.error("[CTF] failed to submit:", err);
        setError("failed to submit flag");
        setSubmitting(false);
      }
    },
    [sessionReady, sendMessage],
  );

  // update settings
  const updateSettings = useCallback(
    (displayName?: string, isOptedIn?: boolean) => {
      if (!sessionReady) {
        console.warn("[CTF] cannot update settings: session not ready");
        return;
      }

      const payload: any = {};
      if (displayName !== undefined) payload.displayName = displayName;
      if (isOptedIn !== undefined) payload.isOptedIn = isOptedIn;

      try {
        sendMessage("ctf", "update_settings", payload);
      } catch (err) {
        console.error("[CTF] failed to update settings:", err);
        setError("failed to update settings");
      }
    },
    [sessionReady, sendMessage],
  );

  // clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // clear result
  const clearResult = useCallback(() => {
    setLastResult(null);
  }, []);

  // ===== SUBSCRIPTIONS =====
  useEffect(() => {
    const unsub1 = onMessage("challenges_list", handleCTFMessage);
    const unsub2 = onMessage("scoreboard_list", handleCTFMessage);
    const unsub3 = onMessage("flag_result", handleCTFMessage);
    const unsub4 = onMessage("settings", handleCTFMessage);
    const unsub5 = onMessage("error", handleCTFMessage);

    return () => {
      unsub1();
      unsub2();
      unsub3();
      unsub4();
      unsub5();
    };
  }, [onMessage, handleCTFMessage]);

  // ===== AUTO-FETCH ON SESSION READY OR RECONNECT =====
  useEffect(() => {
    if (sessionReady) {
      fetchChallenges();
    }
  }, [sessionReady, lastAuthTime, fetchChallenges]);

  const userScore = challenges.reduce((acc, c) => acc + (c.isCompleted ? c.score || 0 : 0), 0);

  const value: CTFContextType = {
    loading,
    scoreboardLoading,
    submitting,
    error,
    challenges,
    scoreboard,
    lastResult,
    userScore,
    settings,

    fetchChallenges,
    fetchScoreboard,
    submitFlag,
    updateSettings,
    clearError,
    clearResult,
  };

  return <CTFContext.Provider value={value}>{children}</CTFContext.Provider>;
}
